/**
 * ServiceOrderScheduleDialog - Diálogo de Agendar/Reagendar OS (BR-OS-032)
 *
 * Extraído da aba Agenda (`ServiceOrderScheduleTab`) para ser reutilizado
 * também pelo botão "Agenda" do cabeçalho do modal, sem duplicar código.
 * Campos: Início, Prestador e Observação. O fim é calculado automaticamente
 * pela duração prevista do tipo de serviço (enviado `end: null` à RPC).
 * Salvar chama `useRescheduleServiceOrder` (RPC existente, sem mudança de backend).
 *
 * RELEASE: 2026-10-10 v1.0.0 - Versão inicial (extração do editor da aba Agenda).
 * RELEASE: 2026-10-10 v1.1.0 - Removido o campo "Fim (opcional)": o fim passa a ser
 *          sempre calculado pela duração do tipo de serviço (envia `end: null`).
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PersonSelector } from '@/components/persons/PersonSelector';
import { format } from 'date-fns';
import { useRescheduleServiceOrder, type ServiceOrderSchedule } from '@/hooks/useServiceOrderSchedules';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  /** Prestador atual da OS (pré-seleção e rótulo) */
  orderProviderId?: string | null;
  orderProviderName?: string | null;
  /** Agendamento existente = reagendar; ausente = novo agendamento */
  schedule?: ServiceOrderSchedule | null;
}

const toLocalInput = (d: string | null | undefined) => (d ? format(new Date(d), "yyyy-MM-dd'T'HH:mm") : '');
const errMsg = (e: unknown) => (e as { message?: string })?.message ?? 'Erro ao salvar';

export function ServiceOrderScheduleDialog({ open, onOpenChange, orderId, orderProviderId, orderProviderName, schedule }: Props) {
  const reschedule = useRescheduleServiceOrder();
  const [form, setForm] = useState({ start: '', providerId: null as string | null, note: '' });
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  // Preenche o formulário uma vez por abertura (sem resetar digitação do usuário)
  const key = open ? `${orderId}:${schedule?.id ?? 'new'}` : null;
  if (key && key !== loadedFor) {
    setLoadedFor(key);
    setForm({
      start: toLocalInput(schedule?.scheduled_start),
      providerId: schedule?.provider_person_id ?? orderProviderId ?? null,
      note: '',
    });
  }

  const save = () => {
    if (!form.start) return toast.error('Informe a data e hora');
    // Agendar exige prestador (também travado no servidor)
    if (!form.providerId) return toast.error('Selecione o prestador para agendar');
    // Fim sempre nulo: a RPC calcula pela duração prevista do tipo de serviço
    reschedule.mutate({
      id: orderId, start: new Date(form.start).toISOString(),
      end: null, providerId: form.providerId, note: form.note,
    }, {
      onSuccess: () => { toast.success(schedule ? 'OS reagendada' : 'OS agendada'); onOpenChange(false); },
      onError: (e) => toast.error(errMsg(e)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{schedule ? 'Reagendar OS' : 'Agendar OS'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Início</Label>
            <Input type="datetime-local" value={form.start} onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))} /></div>
          <p className="text-xs text-muted-foreground">O fim será calculado pela duração prevista do tipo de serviço.</p>
          <div className="space-y-1.5"><Label>Prestador *</Label>
            <PersonSelector role="service_provider" value={form.providerId}
              selectedLabel={form.providerId === orderProviderId ? orderProviderName ?? null : null}
              onSelect={(id) => setForm((f) => ({ ...f, providerId: id }))} placeholder="Selecione o prestador" /></div>
          <div className="space-y-1.5"><Label>Observação</Label>
            <Textarea maxLength={500} rows={3} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={reschedule.isPending}>{reschedule.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
